import { PODCASTERS_SERVICE, poll, Request, toJSON } from '../Request/';
import {
  Category,
  Country,
  DataRange,
  Language,
  Me,
  MeShows,
  PartialBy,
  Podcast,
  PodcastMetadata,
  ShareAction,
  TopEpisode,
} from '../../types';
import uniqBy from 'lodash/uniqBy';
import { TSFix } from '@spotify-internal/ts-migration-utils/types';
import { stringify as qsStringify } from 'query-string';
import {
  HomeCardImpressionEvent,
  HomeCardInteractionEvent,
  CardType,
} from '../Tracking/HomeTracker';

export /* istanbul ignore next */ async function acceptTerms() {
  return await Request.of().post(`${PODCASTERS_SERVICE}/v0/user/acceptTerms`);
}

export class PodcastStillProcessingError extends Error {}

export async function validateSoundtrapHash(hash: string): Promise<Podcast> {
  const { token } = await Request.of()
    .post(`${PODCASTERS_SERVICE}/v0/feeds/evaluateKey`, { token: hash })
    .then(toJSON);

  const body = await poll(() =>
    Request.of().get(`${PODCASTERS_SERVICE}/v0/feeds/evaluate/${token}`),
  );

  const metadata = await body.json();

  if (metadata.shows.length === 0) {
    /**
     * This happens when a Soundtrap user's podcast is still being ingested/processed for metadata.
     */

    throw new PodcastStillProcessingError();
  }

  /**
   * This is basically matching the current evaluation logic found here:
   * https://ghe.spotify.net/creator-plus/podcasters-frontend/blob/5393167f75cb9d912159e898ff3624d88eff1542/src/store/podcastSubmission.js#L118
   *
   * @todo: we need to create an evaluate endpoint and refactor the store/podcastSubmission logic.
   */
  metadata.isExistingShow = true;

  return metadata;
}

export /* istanbul ignore next */ async function claimSoundtrapPodcast(showId: string) {
  return await Request.of().post(`${PODCASTERS_SERVICE}/v0/shows/${showId}/claim`);
}

export /* istanbul ignore next */ async function checkVerificationCode({
  showId,
  code,
}: {
  showId: string;
  code: string;
}) {
  return await Request.of().post(`${PODCASTERS_SERVICE}/v0/shows/${showId}/authorization/${code}`);
}

export async function getCategories(): Promise<Category[]> {
  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/feeds/categories`)
    .then((body: Body) => body.json());
}

export async function getCountries(): Promise<Country[]> {
  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/feeds/countries`)
    .then((body: Body) => body.json());
}

export async function getLanguages(): Promise<Language[]> {
  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/feeds/languages`)
    .then((body: Body) => body.json())
    .then((languages: Language[]) => {
      /**
       * Backend currently sends duplicates of some languages…
       */
      return uniqBy(languages, 'code');
    });
}

export /* istanbul ignore next */ async function updateMetadata(
  showId: string,
  metadata: PodcastMetadata,
) {
  return await Request.of().put(`${PODCASTERS_SERVICE}/v0/shows/${showId}/metadata`, {
    countryOfOrigin: metadata.countryOfOrigin.code,
    language: metadata.language.code,
    categories: metadata.categories,
  });
}

export async function getTopArtistsForPodcast(showId: string) {
  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/shows/${showId}/topArtists`)
    .then((body: Body) => body.json());
}

type ShareEndpoints = { [key in ShareAction]: string };

export const logger = {
  async startedPodcastUpdateFlow(showId: string) {
    await Request.of().post(`${PODCASTERS_SERVICE}/v0/shows/${showId}/logFeedUpdate`);
  },

  async logHomePageLoad() {
    await Request.of().post(`${PODCASTERS_SERVICE}/v0/logLoadHome`);
  },

  async podcastShareAction(showId: string, action: ShareAction) {
    let endpoint: ShareEndpoints = {
      'copy-event': 'logCopyAction',
      'copy-button': 'logCopyLink',
      'download-badges': 'logDownloadBadges',
    };

    try {
      return await Request.of().post(
        `${PODCASTERS_SERVICE}/v0/shows/${showId}/${endpoint[action]}`,
      );
    } catch {
      /* istanbul ignore next */
      return undefined;
    }
  },

  async logHomeCardImpression(body: HomeCardImpressionEvent) {
    await Request.of().post(
      `${PODCASTERS_SERVICE}/v0/logHomeCardImpression/${body.card_type}`,
      body,
    );
  },

  async logHomeCardInteraction(body: HomeCardInteractionEvent) {
    await Request.of().post(
      `${PODCASTERS_SERVICE}/v0/logHomeCardInteraction/${body.card_type}`,
      body,
    );
  },

  async logChartShareCardDownload(body: HomeCardInteractionEvent) {
    await Request.of().post(`${PODCASTERS_SERVICE}/v0/logChartShareCardDownload`, body);
  },
};

export async function loadNetworkAllTimeStats(networkId: string) {
  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/networks/${networkId}/metadata`)
    .then((body: Body) => body.json())
    .then((metadata: TSFix) => {
      return {
        starts: parseInt(metadata.starts, 10),
        streams: parseInt(metadata.streams, 10),
        listeners: parseInt(metadata.listeners, 10),
        followers: parseInt(metadata.followers, 10),
      };
    });
}

export async function getTopEpisodesForNetwork(
  networkId: string,
  dataRange: DataRange,
): Promise<TopEpisode[]> {
  return await Request.of()
    .get(
      `${PODCASTERS_SERVICE}/v0/networks/${networkId}/topEpisodes?start=${dataRange.from}&end=${dataRange.until}`,
    )
    .then((body: Body) => body.json())
    .then((topEpisodes: { episodes: TopEpisode[] }) => topEpisodes.episodes);
}

export async function getTopEpisodesForPodcast(
  podcastId: string,
  dataRange: DataRange,
): Promise<TopEpisode[]> {
  return await Request.of()
    .get(
      `${PODCASTERS_SERVICE}/v0/shows/${podcastId}/topEpisodes?start=${dataRange.from}&end=${dataRange.until}`,
    )
    .then((body: Body) => body.json())
    .then((topEpisodes: { episodes: TopEpisode[] }) => topEpisodes.episodes);
}

type UsersShowsResponse = {
  metadata: {
    network: {
      id: string;
      name: string;
    };
    startDate: string;
    endDate: string;
  };
  shows: MeShows[];
  currentPage: number;
  totalPages: number;
  totalShows: number;
};

export async function getUserShows({
  searchTerm,
  page,
  sortBy,
  sortOrder = 'descending',
  limit = 20,
  networkId,
  start,
  end,
}: {
  start: string;
  end: string;
  searchTerm?: string;
  page?: number;
  sortBy?: string;
  sortOrder?: 'ascending' | 'descending';
  limit?: number;
  networkId?: string;
}): Promise<UsersShowsResponse> {
  const params = qsStringify({
    page,
    filter: searchTerm,
    start,
    end,
    sortBy,
    sortOrder,
    size: limit,
    network: networkId,
  });

  type UsersShowsApiResponse = Omit<UsersShowsResponse, 'shows'> & {
    shows: PartialBy<MeShows, 'starts' | 'streams'>[];
  };

  return await Request.of()
    .get(`${PODCASTERS_SERVICE}/v0/user/shows?${params}`)
    .then((body: Body) => body.json())
    .then((data: UsersShowsApiResponse) => {
      return {
        ...data,
        shows: data.shows.map(show => {
          return {
            ...show,
            starts: show.starts ? show.starts : null,
            streams: show.streams ? show.streams : null,
          };
        }),
      };
    });
}
